import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { GolfAuthLoadingScreen } from '@golfauth/ui';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import {
  isProPasswordGateActive,
  proNeedsPasswordSetup,
} from '../../lib/proPasswordGate';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { professional, isInitialized } = useAuthStore();
  const location = useLocation();
  const [passwordGate, setPasswordGate] = useState<'loading' | 'ok' | 'need'>(
    'loading',
  );

  // Hard gate: Pros cannot use Fitting until a password has been set /
  // reset. Soft redirects in AuthStateHandler alone were racey and
  // admin magic-link skipped them entirely.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!professional) {
        if (!cancelled) setPasswordGate('ok');
        return;
      }

      // Allowlist — these pages own the password flow (or are reached
      // before product features). set/reset are outside ProtectedRoute
      // today; keep the check anyway if routing changes.
      const path = location.pathname;
      if (
        path === '/pro/set-password' ||
        path === '/pro/reset-password' ||
        path === '/set-password' ||
        path === '/reset-password'
      ) {
        if (!cancelled) setPasswordGate('ok');
        return;
      }

      if (isProPasswordGateActive()) {
        if (!cancelled) setPasswordGate('need');
        return;
      }

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled) return;
        if (proNeedsPasswordSetup(session?.user)) {
          setPasswordGate('need');
        } else {
          setPasswordGate('ok');
        }
      } catch {
        if (!cancelled) setPasswordGate('need');
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [professional, location.pathname]);

  if (!isInitialized || (professional && passwordGate === 'loading')) {
    return <GolfAuthLoadingScreen variant="pro" />;
  }

  if (!professional) {
    // Bugbot #293 HIGH: preserve search + hash alongside pathname so
    // deep-links (like the marketplace order QR: /pro/admin?tab=order-
    // management&order=<uuid>&scan=1) survive the login round-trip.
    // Previously we only kept pathname, so QR scans by not-yet-signed-in
    // Pros landed on the admin dashboard with no order pre-selected.
    const fromPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/pro/login" state={{ from: fromPath }} replace />;
  }

  if (passwordGate === 'need') {
    return <Navigate to="/pro/set-password" replace />;
  }

  // Admin approval gate — pending/suspended/blocked pros can sign in but
  // can't reach pro features. /approval-required handles all three states
  // with the right copy. Always allow visiting that page itself.
  const isApprovalPage = location.pathname === '/pro/approval-required';
  if (
    !isApprovalPage &&
    professional.approval_status &&
    professional.approval_status !== 'approved'
  ) {
    return <Navigate to="/pro/approval-required" replace />;
  }

  const isSubscriptionPage = location.pathname === '/pro/subscription-required' || location.pathname === '/pro/subscription-success';

  if (!isSubscriptionPage && (professional.subscription_status === 'grace_period' || professional.subscription_status === 'locked')) {
    return <Navigate to="/pro/subscription-required" replace />;
  }

  return <>{children}</>;
}
